<script setup lang="ts">
import type { FormSettings } from '@/components/interfaces/project-settings-view.interfaces.ts'
import CrdBlock from '@/components/common/CrdBlock.vue'
import { inject, ref } from 'vue'
import { ProjectSettingsField, ProvideElement } from '@/enums.ts'
import { useTranslationStore } from '@/stores/translation.store.ts'
import CrdRange from '@/components/common/CrdRange.vue'

const { t } = useTranslationStore()

function convertSecondsToMinutesWithRounding(seconds: number): string {
  const minutes = Math.ceil(seconds / 60)
  const unit = minutes < 2 ? t('settings.security.minute') : t('settings.security.minutes') // TODO: Add Pluralize
  return `${minutes} ${unit}`
}

// Injections
const { security } = inject(ProvideElement.ProjectSettingsFormSettings) as FormSettings
</script>

<template>
  <div class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
    <!-- 1 -->
    <CrdBlock class="grid sm:col-span-6 gap-2!">
      <h5 class="text-sm md:text-base font-semibold text-center">
        {{ t('settings.security.numberOfMessagesForTimePeriod') }}
      </h5>
      <p class="mt-1 text-sm/6 text-gray-600">
        {{ t('settings.security.numberOfMessagesForTimePeriodDescription') }}
      </p>
      <div class="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
      <div class="sm:col-span-6">
        <CrdRange
          :label=" t('settings.security.messages')"
          :name="ProjectSettingsField.MaxMessagesPerTimeMessages"
          :min="security.maxMessagesPerTimeMessages.min"
          :max="security.maxMessagesPerTimeMessages.max"
          :step="security.maxMessagesPerTimeMessages.step"
          class="w-full mt-2.5"
        />
      </div>
      <div class="sm:col-span-6">
        <CrdRange
          :label="t('settings.security.period')"
          :name="ProjectSettingsField.MaxMessagesPerTimeSeconds"
          :min="security.maxMessagesPerTimeSeconds.min"
          :max="security.maxMessagesPerTimeSeconds.max"
          :step="security.maxMessagesPerTimeSeconds.step"
          class="w-full mt-2.5"
        />
      </div>
    </div>
    </CrdBlock>
    <!-- 2 -->
    <CrdBlock class="grid sm:col-span-6 gap-2!">
      <h5 class="text-sm md:text-base font-semibold text-center">
        {{ t('settings.security.numberOfNewChatsForTimePeriod') }}
      </h5>
      <p class="mt-1 text-sm/6 text-gray-600">
        {{ t('settings.security.numberOfNewChatsForTimePeriodDescription') }}
      </p>
      <div class="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
        <div class="sm:col-span-6">
          <CrdRange
            :label="t('settings.security.chats')"
            :name="ProjectSettingsField.MaxChatsPerTimeChats"
            :min="security.maxChatsPerTimeChats.min"
            :max="security.maxChatsPerTimeChats.max"
            :step="security.maxChatsPerTimeChats.step"
            class="w-full mt-2.5"
          />
        </div>
        <div class="sm:col-span-6">
          <CrdRange
            :label="t('settings.security.period')"
            :name="ProjectSettingsField.MaxChatsPerTimeSeconds"
            :min="security.maxChatsPerTimeSeconds.min"
            :max="security.maxChatsPerTimeSeconds.max"
            :step="security.maxChatsPerTimeSeconds.step"
            class="w-full mt-2.5"
          />
        </div>
      </div>
    </CrdBlock>
    <!-- 3 -->
    <CrdBlock class="grid sm:col-span-6 gap-2!" >
      <h5 class="text-sm md:text-base font-semibold text-center">
        {{ t('settings.security.maximumNewChatsFromSingleIPForTimePeriod') }}
      </h5>
      <p class="mt-1 text-sm/6 text-gray-600">
        {{ t('settings.security.maximumNewChatsFromSingleIPForTimePeriodDescription') }}
      </p>
      <div class="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
        <div class="sm:col-span-6">
          <CrdRange
            :label="t('settings.security.chats')"
            :name="ProjectSettingsField.BanIpChatsLimitChats"
            :min="security.banIpChatsLimitChats.min"
            :max="security.banIpChatsLimitChats.max"
            :step="security.banIpChatsLimitChats.step"
            class="w-full mt-2.5"
          />
        </div>
        <div class="sm:col-span-6">
          <CrdRange
            :label="t('settings.security.period')"
            :name="ProjectSettingsField.BanIpChatsLimitIntervalSeconds"
            :min="security.banIpChatsLimitChats.min"
            :max="security.banIpChatsLimitChats.max"
            :step="security.banIpChatsLimitChats.step"
            class="w-full mt-2.5"
          />
        </div>
        <div class="sm:col-span-6">
          <CrdRange
            :label="t('settings.security.banTime')"
            :name="ProjectSettingsField.BanIpChatsLimitIntervalBanTimeMinutes"
            :min="security.banIpChatsLimitIntervalBanTimeMinutes.min"
            :max="security.banIpChatsLimitIntervalBanTimeMinutes.max"
            :step="security.banIpChatsLimitIntervalBanTimeMinutes.step"
            class="w-full mt-2.5"
          />
        </div>
      </div>
    </CrdBlock>
    <!-- 4 -->
    <CrdBlock class="grid sm:col-span-6 gap-2!">
      <h5 class="text-sm md:text-base font-semibold text-center">
        {{ t('settings.security.maximumNumberOfMessagesInSingleChatPerPeriodOfTime') }}
      </h5>
      <p class="mt-1 text-sm/6 text-gray-600">
        {{ t('settings.security.maximumNumberOfMessagesInSingleChatPerPeriodOfTimeDescription') }}
      </p>
      <div class="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
        <div class="sm:col-span-6">
          <CrdRange
            :label="t('settings.security.period')"
            :name="ProjectSettingsField.MaxChatMessagesPerTimeSeconds"
            :min="security.maxChatMessagesPerTimeSeconds.min"
            :max="security.maxChatMessagesPerTimeSeconds.max"
            :step="security.maxChatMessagesPerTimeSeconds.step"
            class="w-full mt-2.5"
          />
        </div>
        <div class="sm:col-span-6">
          <CrdRange
            :label="t('settings.security.period')"
            :name="ProjectSettingsField.MaxChatMessagesPerTimeMessages"
            :min="security.maxChatMessagesPerTimeMessages.min"
            :max="security.maxChatMessagesPerTimeMessages.max"
            :step="security.maxChatMessagesPerTimeMessages.step"
            class="w-full mt-2.5"
          />
        </div>
        <div class="sm:col-span-6">
          <CrdRange
            :label="t('settings.security.banTime')"
            :name="ProjectSettingsField.MaxChatMessagesPerTimeBanMinutes"
            :min="security.maxChatMessagesPerTimeBanMinutes.min"
            :max="security.maxChatMessagesPerTimeBanMinutes.max"
            :step="security.maxChatMessagesPerTimeBanMinutes.step"
            class="w-full mt-2.5"
          />
        </div>
      </div>
    </CrdBlock>
    <!-- 5 -->
    <CrdBlock class="grid sm:col-span-6 gap-2!">
      <h5 class="text-sm md:text-base font-semibold text-center">
        {{ t('settings.security.messagesLimitFromAIPerChat') }}
      </h5>
      <p class="mt-1 text-sm/6 text-gray-600">
        {{ t('settings.security.messagesLimitFromAIPerChatDescription') }}
      </p>
      <div class="mt-1 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
      <div class="sm:col-span-6">
        <CrdRange
          :label="t('settings.security.messagesLimit')"
          :name="ProjectSettingsField.AiQuitMessagesThreshold"
          :min="security.aiQuitMessagesThreshold.min"
          :max="security.aiQuitMessagesThreshold.max"
          :step="security.aiQuitMessagesThreshold.step"
          class="w-full mt-2.5"
        />
      </div>
    </div>
    </CrdBlock>
  </div>
</template>
